import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender
} from '@tanstack/react-table';

const PanelListClassic = ({ panels, header, searchProps }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [tableData, setTableData] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const filterFields = ["panelId", "environment", "address", "city", "design", "status"];

    useEffect(() => {
        const firstImageUrl = p => (p.postingImageUrls && p.postingImageUrls[0]) || '';
        setTableData([...panels].sort((a, b) => (firstImageUrl(a) < firstImageUrl(b) ? 1 : -1)));
    }, [panels]);

    const filteredData = useMemo(() => {
        return tableData.filter(row =>
            filterFields.some(field =>
                row[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [tableData, searchTerm]);

    const columns = useMemo(() => {
        const imageExists = panels.some(p => p.postingImageUrls?.length);
        return [
            { accessorKey: 'panelId', header: 'Panel Id', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'environment', header: 'Type', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'address', header: 'Address', cell: info => <div className="classicPanelList address">{info.getValue()}</div>, enableSorting: true },
            { accessorKey: 'city', header: 'City', cell: info => info.getValue(), enableSorting: true },
            { accessorKey: 'design', header: 'Design', cell: info => info.getValue(), enableSorting: true },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => `${row.original.status} ${moment(row.original.timeStamp).format('YYYY-MM-DD HH:mm')}`,
                enableSorting: true
            },
            ...(imageExists ? [{
                accessorKey: 'postingImageUrls',
                header: 'Picture',
                cell: ({ row }) => row.original.postingImageUrls?.length ? (
                    row.original.postingImageUrls.map((url, index) => (
                        <ImageToggle key={index} postingImageUrl={url} panelId={row.original.panelId} />
                    ))
                ) : null,
                enableSorting: true
            }] : [])
        ];
    }, [panels]);

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: {
            pagination: {
                pageSize: 50
            }
        },
        manualPagination: showAll, // Disable pagination when showing all
    });

    return (
        <div className="classicPanelList hidden-print">
            <div className="stickyHeader">
                <h4>{header}</h4>
                <input
                    type="text"
                    className="searchBar"
                    placeholder="Filter"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div className="tableContainer">
                <table className="table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    const sortOrder = header.column.getIsSorted();
                                    return (
                                        <th key={header.id} onClick={header.column.getToggleSortingHandler()} className="sortableHeader">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <span className="sortArrow">
                                                {sortOrder === 'asc' ? ' ↑' : sortOrder === 'desc' ? ' ↓' : ' ↑↓'}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {(showAll ? table.getPrePaginationRowModel().rows : table.getRowModel().rows).map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="paginationContainer">
                {!showAll ? (
                    <>
                        <button className="btn btn-posting paginationButton" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                            &lt;
                        </button>
                        <span> Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} </span>
                        <button className="btn btn-posting paginationButton" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                            &gt;
                        </button>
                        <button className="btn btn-posting showAllButton trigger-pagination" onClick={() => setShowAll(true)}>
                            Show All
                        </button>
                    </>
                ) : (
                        <button className="btn btn-posting paginationButton trigger-pagination" onClick={() => setShowAll(false)}>
                        Back to Paginated View
                    </button>
                )}
            </div>
        </div>
    );
};

const ImageToggle = ({ postingImageUrl, panelId }) => {
    const [isLarge, setIsLarge] = useState(false);
    const toggleImageSize = useCallback(() => {
        setIsLarge(!isLarge);
    }, [isLarge]);

    return postingImageUrl ? (
        <img
            src={postingImageUrl}
            alt={`Panel ${panelId}`}
            className={isLarge ? "classicPanelList img-smal img-large" : "classicPanelList img-smal"}
            onClick={toggleImageSize}
        />
    ) : null;
};

export default PanelListClassic;
